import { useEffect, useMemo, useRef, useState } from 'react'

export const useScrollspy = (slugs: string[]) => {
  const elementsRef = useRef<Map<string, HTMLElement>>(new Map())
  const [activeSlug, setActiveSlug] = useState<string | undefined>(slugs[0])

  const refs = useMemo(
    () =>
      slugs.reduce<Record<string, (node: HTMLElement | null) => () => void>>((acc, slug) => {
        acc[slug] = (node: HTMLElement | null) => {
          if (node) elementsRef.current.set(slug, node)
          return () => {
            elementsRef.current.delete(slug)
          }
        }
        return acc
      }, {}),
    [slugs],
  )

  useEffect(() => {
    let frame = 0

    const checkActiveSlug = () => {
      frame = 0
      let curr = slugs[0]
      for (const slug of slugs) {
        const el = elementsRef.current.get(slug)
        if (!el) continue
        if (el.getBoundingClientRect().top <= window.innerHeight * 0.2) curr = slug
      }
      setActiveSlug((prev) => (prev === curr ? prev : curr))
    }

    const debounce = () => {
      if (frame) return
      frame = requestAnimationFrame(checkActiveSlug)
    }

    debounce()
    window.addEventListener('scroll', debounce, { passive: true })
    window.addEventListener('resize', debounce)

    return () => {
      if (frame) cancelAnimationFrame(frame)
      window.removeEventListener('scroll', debounce)
      window.removeEventListener('resize', debounce)
    }
  }, [slugs])

  return { activeSlug, refs }
}
